import { useState, type DragEvent } from 'react'
import { MaterialIcon } from '@/ui/material-icon'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/tooltip'
import type { BucketedPaletteConnectors, PaletteConnectorItem } from '@/features/adp/api/dataSourcePaletteBuckets'
import type { DragPayload } from '@/features/adp/utils/pipelineAssetUtils'
import styles from '@/features/adp/pages/AdpPipelinePage/AdpPipelinePage.module.scss'

export type DataSourcePaletteState = {
  fileSystemOpen: boolean
  setFileSystemOpen: (open: boolean) => void
  databaseOpen: boolean
  setDatabaseOpen: (open: boolean) => void
  restApiOpen: boolean
  setRestApiOpen: (open: boolean) => void
  nosqlOpen: boolean
  setNosqlOpen: (open: boolean) => void
}

export function useDataSourcePaletteState(fileSystemDefault = true): DataSourcePaletteState {
  const [fileSystemOpen, setFileSystemOpen] = useState(fileSystemDefault)
  const [databaseOpen, setDatabaseOpen] = useState(false)
  const [restApiOpen, setRestApiOpen] = useState(false)
  const [nosqlOpen, setNosqlOpen] = useState(false)
  return {
    fileSystemOpen,
    setFileSystemOpen,
    databaseOpen,
    setDatabaseOpen,
    restApiOpen,
    setRestApiOpen,
    nosqlOpen,
    setNosqlOpen,
  }
}

type DataSourcePaletteProps = {
  groupId: string
  palette: DataSourcePaletteState
  onPaletteDragStart: (e: DragEvent, payload: DragPayload) => void
  connectors: BucketedPaletteConnectors
  isLoading?: boolean
  loadError?: string | null
}

type PaletteKey = keyof BucketedPaletteConnectors
const ICON_FILENAME_FALLBACK_MAP: Record<string, string> = {
  'postgresql.svg': 'postgres.svg',
  'mariadb.svg': 'maria-db.svg',
  'mongodb.svg': 'mongo-db.svg',
  'sql-generic.svg': 'mssql.svg',
}

function ConnectorGrid({
  items,
  groupId,
  dragIcon,
  onPaletteDragStart,
}: {
  items: PaletteConnectorItem[]
  groupId: string
  dragIcon: string
  onPaletteDragStart: (e: DragEvent, payload: DragPayload) => void
}) {
  if (items.length === 0) {
    return <div className={styles.paletteEmpty}>No connectors in this category.</div>
  }
  return (
    <div className={styles.fileSystemGrid}>
      {items.map(item => (
        <Tooltip key={`${item.dataSourceId}-${item.id}`}>
          <TooltipTrigger asChild>
            <div
              className={styles.fileSystemTile}
              draggable
              onDragStart={e =>
                onPaletteDragStart(e, {
                  label: item.label,
                  groupId,
                  connectorId: item.id,
                  iconImage: item.image || undefined,
                  icon: dragIcon,
                  dataSourceId: item.dataSourceId,
                })
              }
            >
              <div className={styles.fileSystemTileInner}>
                {item.image ? (
                  <img
                    src={item.image}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    onError={e => {
                      const target = e.target as HTMLImageElement
                      if (target.dataset.fallback === 'done') return
                      const filename = item.image.split('/').pop() ?? ''
                      const normalized = ICON_FILENAME_FALLBACK_MAP[filename] ?? filename
                      const localSvg = `/assets/img/icons/db/${normalized}`
                      const localPng = `/assets/img/icons/db/${normalized.replace('.svg', '.png')}`
                      if (target.dataset.fallback !== 'svg') {
                        target.dataset.fallback = 'svg'
                        target.src = localSvg
                      } else {
                        target.dataset.fallback = 'done'
                        target.src = localPng
                      }
                    }}
                  />
                ) : (
                  <MaterialIcon name={dragIcon} size={24} />
                )}
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className={styles.fileSystemTooltip}>
            {item.label}
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  )
}

const SECTIONS: {
  key: PaletteKey
  label: string
  collapsedIcon: string
  dragIcon: string
  databaseBar: boolean
  fileSystemFolderInBar: boolean
  wrapperClass: string
}[] = [
    {
      key: 'fileSystem',
      label: 'File System',
      collapsedIcon: 'folder',
      dragIcon: 'folder',
      databaseBar: false,
      fileSystemFolderInBar: true,
      wrapperClass: styles.fileSystemSection,
    },
    {
      key: 'database',
      label: 'Database',
      collapsedIcon: 'storage',
      dragIcon: 'storage',
      databaseBar: true,
      fileSystemFolderInBar: false,
      wrapperClass: styles.databaseSection,
    },
    {
      key: 'restApi',
      label: 'Rest APIs',
      collapsedIcon: 'api',
      dragIcon: 'api',
      databaseBar: true,
      fileSystemFolderInBar: false,
      wrapperClass: styles.restApiSection,
    },
    {
      key: 'nosql',
      label: 'No SQL Database',
      collapsedIcon: 'dataset',
      dragIcon: 'dataset',
      databaseBar: true,
      fileSystemFolderInBar: false,
      wrapperClass: styles.nosqlSection,
    },
  ]

function sectionOpenState(
  key: PaletteKey,
  p: DataSourcePaletteState
): [boolean, (v: boolean) => void] {
  switch (key) {
    case 'fileSystem':
      return [p.fileSystemOpen, p.setFileSystemOpen]
    case 'database':
      return [p.databaseOpen, p.setDatabaseOpen]
    case 'restApi':
      return [p.restApiOpen, p.setRestApiOpen]
    case 'nosql':
      return [p.nosqlOpen, p.setNosqlOpen]
  }
}

export function DataSourcePalette({
  groupId,
  palette: p,
  onPaletteDragStart,
  connectors,
  isLoading,
  loadError,
}: Readonly<DataSourcePaletteProps>) {
  const visibleSections =
    groupId === 'destinations'
      ? SECTIONS.filter((section) => section.key !== 'restApi')
      : SECTIONS

  return (
    <div className={styles.sourcesNested}>
      {(isLoading || loadError) && (
        <div className={styles.paletteStatus}>
          {isLoading && <span>Loading data sources…</span>}
          {loadError && (
            <span className={styles.paletteStatusError} role="alert">
              {loadError}
            </span>
          )}
        </div>
      )}
      {visibleSections.map(meta => {
        const [open, setOpen] = sectionOpenState(meta.key, p)
        const items = connectors[meta.key]
        return (
          <div key={meta.key} className={meta.wrapperClass}>
            {open ? (
              <div className={styles.fileSystemCard}>
                <button
                  type="button"
                  className={`${styles.fileSystemBar} ${meta.databaseBar ? styles.databaseBar : ''}`}
                  onClick={() => setOpen(false)}
                  aria-expanded
                >
                  {meta.fileSystemFolderInBar && (
                    <MaterialIcon name="folder" size={20} className={styles.fileSystemBarGlyph} />
                  )}
                  <span className={styles.fileSystemBarLabel}>{meta.label}</span>
                  <MaterialIcon name="expand_more" size={20} className={styles.fileSystemBarGlyph} />
                </button>
                <ConnectorGrid
                  items={items}
                  groupId={groupId}
                  dragIcon={meta.dragIcon}
                  onPaletteDragStart={onPaletteDragStart}
                />
              </div>
            ) : (
              <button
                type="button"
                className={styles.fileSystemCollapsed}
                onClick={() => setOpen(true)}
                aria-expanded={false}
              >
                <MaterialIcon name={meta.collapsedIcon} size={18} />
                <span className={styles.fileSystemCollapsedLabel}>{meta.label}</span>
                <MaterialIcon name="chevron_right" size={18} />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}